// "Me": what the signed-in person may do, and where they work (spec/07 §6.3, §7; spec/08 §4.2).
//
//   GET /v1/me/access?venueId=|organizationId=   the effective permission set at one scope
//   GET /v1/me/access                            at platform scope: the platform role, its `hq:*`
//                                                bundle, and the second factor HQ always needs
//   GET /v1/me/organizations                     the organisations the person is on the staff of
//   GET /v1/me/venues[?organizationId=]          the live venues they work at
//
// All four through the generated client (F-IAM-11, #466). Answers are still read defensively: a
// response without a permission set grants nothing, and a malformed row is left out rather than
// shown. The console only *shows* from these; the server checks every request again (spec/13 §1).

import type {
  ApiClient,
  MyAccess as MyAccessResponse,
  MyOrganization as MyOrganizationResponse,
  MyVenue as MyVenueResponse,
} from '@clavian/api-client';

export type { MyAccessResponse, MyOrganizationResponse, MyVenueResponse };

// ---------------------------------------------------------------------------------------------
// GET /v1/me/access (spec/07 §6.3)
// ---------------------------------------------------------------------------------------------

export type AccessScope =
  | { readonly venueId: string; readonly organizationId?: undefined }
  | { readonly organizationId: string; readonly venueId?: undefined };

/** What the console keeps of it. */
export interface MyAccess {
  readonly role: string | null;
  readonly permissions: ReadonlySet<string>;
  /** The membership at this scope: `member:{id}` is the channel this member's `command.updated` arrives on. */
  readonly memberId: string | null;
  /** Which Clavian products the organisation has; absent when the api did not say (an older one). */
  readonly products: { readonly stations: boolean; readonly boot: boolean } | null;
}

const permissionSet = (body: Partial<MyAccessResponse> | null | undefined): ReadonlySet<string> => {
  const permissions: readonly unknown[] = Array.isArray(body?.permissions) ? body.permissions : [];
  return new Set(permissions.filter((code): code is string => typeof code === 'string'));
};

export async function fetchMyAccess(api: ApiClient, scope: AccessScope): Promise<MyAccess> {
  const query = scope.venueId === undefined ? { organizationId: scope.organizationId } : { venueId: scope.venueId };
  const body = (await api.getMyAccess({ query })) as Partial<MyAccessResponse> | null;
  // Read defensively all the same: an answer without a permission set grants nothing.
  return {
    role: typeof body?.role === 'string' ? body.role : null,
    permissions: permissionSet(body),
    memberId: typeof body?.memberId === 'string' ? body.memberId : null,
    products: body?.products === undefined ? null : { stations: body.products.stations === true, boot: body.products.boot === true },
  };
}

/** What the HQ console keeps of the platform-scope answer (spec/07 §7, spec/14 §1). */
export interface PlatformAccess {
  /** The platform role — `hq_owner`, `hq_admin`… — or `null` for a person who holds none. */
  readonly role: string | null;
  /** Its `hq:*` bundle (and `audit:read`), exactly what the HQ routes' permission checks allow. */
  readonly permissions: ReadonlySet<string>;
  /** Every platform role needs a second factor; whether this session has one. */
  readonly secondFactor: { readonly required: boolean; readonly satisfied: boolean };
}

export async function fetchPlatformAccess(api: ApiClient): Promise<PlatformAccess> {
  const body = (await api.getMyAccess({})) as Partial<MyAccessResponse> | null;
  // An answer that is not about the platform describes nobody's HQ access.
  const platform = body?.scope === 'platform';
  const role = platform && typeof body?.role === 'string' && body.role !== '' ? body.role : null;
  const secondFactor = body?.secondFactor;
  return {
    role,
    permissions: platform ? permissionSet(body) : new Set(),
    secondFactor: {
      // A platform role always needs one (spec/07 §6.6); an answer that forgot to say so still does.
      required: role !== null || secondFactor?.required === true,
      satisfied: secondFactor?.satisfied === true,
    },
  };
}

// ---------------------------------------------------------------------------------------------
// GET /v1/me/organizations and GET /v1/me/venues (spec/08 §4.2)
// ---------------------------------------------------------------------------------------------

/** What the console keeps of an organisation. */
export interface MyOrganization {
  readonly id: string;
  readonly slug: string;
  readonly name: string;
  readonly role?: string;
  readonly allVenues?: boolean;
}

/** What the console keeps of a venue. */
export interface MyVenue {
  readonly id: string;
  readonly slug: string;
  readonly name: string;
  readonly organizationId: string;
  readonly timezone?: string;
  readonly currency?: string;
  /** `setup`, `live` or `paused` (an archived venue is never listed): whether it has opened (spec/13 §13.1). */
  readonly status?: 'setup' | 'live' | 'paused';
}

const text = (row: Record<string, unknown>, key: string): string | undefined =>
  typeof row[key] === 'string' ? (row[key] as string) : undefined;

/** By name, as the server orders them. Empty for someone on no staff. */
export async function fetchMyOrganizations(api: ApiClient): Promise<readonly MyOrganization[]> {
  const body: unknown = await api.listMyOrganizations();
  return rows(body).flatMap((row) => {
    const id = text(row, 'id');
    const slug = text(row, 'slug');
    if (id === undefined || slug === undefined) return [];
    const role = text(row, 'role');
    return [
      {
        id,
        slug,
        name: text(row, 'name') ?? slug,
        ...(role === undefined ? {} : { role }),
        ...(typeof row['allVenues'] === 'boolean' ? { allVenues: row['allVenues'] } : {}),
      },
    ];
  });
}

/** The live venues the member works at, by organisation; one organisation's with `organizationId`. */
export async function fetchMyVenues(api: ApiClient, organizationId?: string): Promise<readonly MyVenue[]> {
  const body: unknown = await api.listMyVenues(organizationId === undefined ? {} : { query: { organizationId } });
  return rows(body).flatMap((row) => {
    const id = text(row, 'id');
    const slug = text(row, 'slug');
    const organization = text(row, 'organizationId');
    if (id === undefined || slug === undefined || organization === undefined) return [];
    const timezone = text(row, 'timezone');
    const currency = text(row, 'currency');
    const status = text(row, 'status');
    return [
      {
        id,
        slug,
        name: text(row, 'name') ?? slug,
        organizationId: organization,
        ...(timezone === undefined ? {} : { timezone }),
        ...(currency === undefined ? {} : { currency }),
        ...(status === 'setup' || status === 'live' || status === 'paused' ? { status } : {}),
      },
    ];
  });
}

// ---------------------------------------------------------------------------------------------

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** A list is `{ data: [...] }` (spec/08 §2.3); these two carry no page, being short. */
function rows(body: unknown): Record<string, unknown>[] {
  const data = isRecord(body) ? body['data'] : undefined;
  return Array.isArray(data) ? data.filter(isRecord) : [];
}
